/**
 * Sandbox Persistence API Client
 * 
 * Handles communication with the sandbox-backend for saving, loading,
 * and forking sandboxes. Integrates with URL routing for /<hash>/<revision> URLs.
 */

import { 
  createApiClient, 
  type SandboxApiClient,
  type SaveSandboxResponse,
  type LoadSandboxResponse,
  type SandboxListResponse,
  type SandboxListOptions,
  type GboxDocument,
  parseSandboxPath,
  buildSandboxPath,
} from '@gotti/sandbox-shared';

/**
 * Get the backend API URL from environment or default
 */
export function getApiBaseUrl(): string {
  // In development, use the default backend URL
  // In production, use the same origin or configured URL
  if (import.meta.env.VITE_SANDBOX_API_URL) {
    return import.meta.env.VITE_SANDBOX_API_URL;
  }
  if (import.meta.env.DEV) {
    return 'http://localhost:3844';
  }
  return window.location.origin;
}

/**
 * CRUD base URL. When VITE_ENGINE_API_URL is set (build-time), sandbox CRUD targets
 * engine-api; assets/agent keep using getApiBaseUrl() (legacy). This is a BUILD-TIME
 * deployment target, not a runtime feature flag — the built bundle uses exactly one.
 */
export function getCrudBaseUrl(): string {
  if (import.meta.env.VITE_ENGINE_API_URL) {
    return import.meta.env.VITE_ENGINE_API_URL as string;
  }
  return getApiBaseUrl();
}

/** Per-sandbox edit tokens in localStorage, keyed `gotti.sandbox.editToken.<id>`. */
const EDIT_TOKEN_PREFIX = 'gotti.sandbox.editToken.';
export const localEditTokenStore = {
  get(id: string): string | undefined {
    try { return localStorage.getItem(EDIT_TOKEN_PREFIX + id) ?? undefined; } catch { return undefined; }
  },
  set(id: string, token: string): void {
    try { localStorage.setItem(EDIT_TOKEN_PREFIX + id, token); } catch { /* private mode / quota — non-fatal */ }
  },
};

/**
 * Singleton API client instance
 */
let apiClient: SandboxApiClient | null = null;

/**
 * Get the API client instance
 */
export function getApiClient(): SandboxApiClient {
  if (!apiClient) {
    apiClient = createApiClient(getCrudBaseUrl(), { editTokenStore: localEditTokenStore });
  }
  return apiClient;
}

/**
 * Parse the current URL to extract sandbox hash and revision
 */
export function getCurrentSandboxFromUrl(): { hash: string; revision?: number } | null {
  const path = window.location.pathname;
  return parseSandboxPath(path);
}

/**
 * Update the browser URL to reflect the current sandbox
 */
export function updateUrlForSandbox(hash: string, revision?: number): void {
  const path = buildSandboxPath(hash, revision);
  window.history.pushState({ hash, revision }, '', path);
}

/**
 * Replace the browser URL without adding to history
 */
export function replaceUrlForSandbox(hash: string, revision?: number): void {
  const path = buildSandboxPath(hash, revision);
  window.history.replaceState({ hash, revision }, '', path);
}

/**
 * Navigate to a sandbox by updating the URL
 */
export function navigateToSandbox(hash: string, revision?: number): void {
  updateUrlForSandbox(hash, revision);
  // Dispatch a custom event so the app can respond
  window.dispatchEvent(new CustomEvent('sandbox-navigate', { 
    detail: { hash, revision } 
  }));
}

/**
 * Save the current sandbox to the backend
 */
export async function saveSandbox(
  data: GboxDocument,
  parentHash?: string
): Promise<SaveSandboxResponse> {
  const client = getApiClient();
  const response = await client.save({
    data,
    parentHash,
  });
  
  // Update the URL to the new sandbox
  updateUrlForSandbox(response.hash, response.revision);
  
  return response;
}

/**
 * Save a new revision of an existing sandbox
 */
export async function saveNewRevision(
  data: GboxDocument,
  parentHash: string,
  expectedHeadRevision?: number,
): Promise<SaveSandboxResponse> {
  const client = getApiClient();
  const response = await client.save({
    data,
    parentHash,
    fork: false,
    expectedHeadRevision,
  });

  // Update the URL to the new revision
  updateUrlForSandbox(response.hash, response.revision);

  return response;
}

/**
 * Load a sandbox from the backend
 */
export async function loadSandbox(
  hash: string,
  revision?: number
): Promise<LoadSandboxResponse> {
  const client = getApiClient();
  return client.load(hash, revision);
}

/**
 * Fork a sandbox (copies stored version)
 */
export async function forkSandbox(
  hash: string,
  revision?: number
): Promise<SaveSandboxResponse> {
  const client = getApiClient();
  const response = await client.fork(hash, revision);
  
  // Navigate to the forked sandbox
  updateUrlForSandbox(response.hash, response.revision);
  
  return response;
}

/**
 * Fork with current content - creates new hash from provided data
 * Unlike forkSandbox which copies stored version, this saves current editor state
 */
export async function forkWithContent(
  data: GboxDocument,
  parentHash?: string
): Promise<SaveSandboxResponse> {
  const client = getApiClient();
  const response = await client.save({
    data,
    parentHash,
    fork: true,  // This tells backend to create new hash
  });
  
  // Navigate to the forked sandbox
  updateUrlForSandbox(response.hash, response.revision);
  
  return response;
}

/**
 * List sandboxes from the backend
 */
export async function listSandboxes(
  options?: SandboxListOptions
): Promise<SandboxListResponse> {
  const client = getApiClient();
  return client.list(options);
}

/**
 * Check if the backend is available
 */
export async function checkBackendHealth(): Promise<boolean> {
  try {
    const engine = import.meta.env.VITE_ENGINE_API_URL as string | undefined;
    const url = engine ? `${engine}/health/live` : `${getApiBaseUrl()}/api/health`;
    const response = await fetch(url);
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Generate a shareable URL for a sandbox
 */
export function getShareUrl(hash: string, revision?: number): string {
  const baseUrl = window.location.origin;
  const path = buildSandboxPath(hash, revision);
  return `${baseUrl}${path}`;
}

/**
 * Copy the share URL to clipboard
 */
export async function copyShareUrl(hash: string, revision?: number): Promise<boolean> {
  try {
    const url = getShareUrl(hash, revision);
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}
